import { useEffect, useState } from 'react';
import { teacherAnalyticsAPI } from '../api/teacherAnalyticsAPI';
import { organisationsAPI } from '../../organisations/api/organisationsAPI';

/**
 * The Overview tab's data for one scope and period: tracker usage rows
 * (GET /api/tracker/teacher/dashboard: the Needs attention buckets, quiz
 * accuracy, documents) and active students and items completed per day
 * (/api/teacher-analytics/overview), both asked for the same `period`,
 * and each student's documents on file (GET /api/organisations/documents,
 * not filtered by period): the rows the Documents drill-down lists, so
 * Document coverage counts the same rows. Loaded only while `enabled`,
 * since the tracker payload is the heaviest request the dashboard makes
 * and only this tab reads it.
 *
 * "Most engaged" is not loaded here: it ranks the Students list's rows
 * (useTeacherDashboardData), which carry every student's completions and
 * active days, so no student is left out by a leaderboard's row limit.
 */
const EMPTY = {
  key: null, students: [], overview: null, documents: [], error: '',
};

const useOverviewData = ({ scope, period, enabled }) => {
  // Keyed by what was asked for, so the tab reads as loading from the first
  // render for a new scope or period, never briefly as "no activity".
  const key = `${scope}|${period}`;
  const [result, setResult] = useState(EMPTY);

  useEffect(() => {
    if (!enabled || !scope) return undefined;
    let cancelled = false;
    Promise.all([
      teacherAnalyticsAPI.getTrackerDashboard({ period, scope }),
      teacherAnalyticsAPI.getOverview({ period, scope }),
      organisationsAPI.getDocuments({ scope }),
    ])
      .then(([tracker, overview, documents]) => {
        if (cancelled) return;
        setResult({
          key, students: tracker?.students || [], overview, documents: documents?.students || [], error: '',
        });
      })
      .catch(() => {
        if (!cancelled) setResult({ ...EMPTY, key, error: 'Could not load the overview. Please try again.' });
      });
    return () => { cancelled = true; };
  }, [enabled, scope, period, key]);

  const current = result.key === key ? result : EMPTY;
  return {
    students: current.students,
    overview: current.overview,
    documents: current.documents,
    error: current.error,
    loading: Boolean(enabled && scope) && result.key !== key,
  };
};

export default useOverviewData;
